import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEditCtaContext } from "../../../../../../context/EditCtaContext";
import { useParams } from "react-router-dom";
import { createCta } from "../../../../../../services/elementServices";
import { toast } from "react-toastify";
import { VerticalTemplates } from "../../../../../../services/Templates";
import logger from '../../../../../../utils/logger';

const PlacementGroup = () => {
  const {
    formData,
    setFormData,
    selectedTemplateKey,
    presetEditId,
    setPresetEditId,
    getCurrentStep,
  } = useEditCtaContext();
  const [isSaving, setIsSaving] = useState(false);
  const { folderId } = useParams();
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async () => {
    if (!formData.elementName?.trim()) {
      alert("Please enter an element name");
      return;
    }

    if (!folderId) {
      alert("Please select a folder");
      return;
    }

    setIsSaving(true);
    logger("log", formData, "formData");
    try {
      const data = VerticalTemplates[selectedTemplateKey].submitForm({
        ...formData,
        name: formData.elementName,
        folderId,
        displayStyle: selectedTemplateKey,
        id: presetEditId,
      });
      await createCta(data);
      toast.success("Preset created Successfully");
      setPresetEditId(null);
      navigate(`/dashboard/folders/element/${folderId}`);
    } catch (error) {
      toast.error("Error on Creating Preset");
      logger("error", "Error saving:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col px-6 pb-6">
      {/* Step Header */}
      <div className="flex flex-col pt-4 mb-6">
        <h2 className="text-white text-2xl font-semibold leading-[30px] tracking-[-0.15px] mb-0">
          Step {getCurrentStep() + 1}
        </h2>
        <p className="text-white text-xl font-medium leading-6">
          Placement & Save
        </p>
      </div>

      {/* Form Fields */}
      <div className="flex flex-col gap-4">
        {/* Choose Placement - hidden for centered templates. For horizontal templates show Top/Bottom instead of Left/Right */}
        {VerticalTemplates[selectedTemplateKey]?.type !== "centered" && (
          <div className="flex flex-col gap-2">
            <label className="text-white text-base font-bold leading-[22px] tracking-[-0.18px]">
              Choose Placement
            </label>

            <div className="flex justify-center items-center gap-5 mt-2">
              {/* First Option (Left or Top) */}
              <button
                type="button"
                onClick={() =>
                  handleChange(
                    "placement",
                    VerticalTemplates[selectedTemplateKey]?.type ===
                      "horizontal"
                      ? "top"
                      : "left"
                  )
                }
                className={`flex flex-col items-center gap-0.5 transition-all p-3 ${
                  (
                    VerticalTemplates[selectedTemplateKey]?.type ===
                    "horizontal"
                      ? formData.placement === "top"
                      : formData.placement === "left"
                  )
                    ? "opacity-100 ring-2 ring-[#783DF9] rounded shadow-[0_0_8px_rgba(120,61,249,0.6)]"
                    : "opacity-60"
                }`}
              >
                <div className={`relative`}>
                  <img
                    src={
                      VerticalTemplates[selectedTemplateKey]?.type ===
                      "horizontal"
                        ? "/horizontal-top.png"
                        : "/vertical-left.svg"
                    }
                    alt={
                      VerticalTemplates[selectedTemplateKey]?.type ===
                      "horizontal"
                        ? "Top placement"
                        : "Left placement"
                    }
                    className="w-[104px] h-[76px]"
                  />
                </div>
                <span className="text-white text-[13px] font-medium">
                  {VerticalTemplates[selectedTemplateKey]?.type === "horizontal"
                    ? "Top"
                    : "Left"}
                </span>
              </button>

              {/* Second Option (Right or Bottom) */}
              <button
                type="button"
                onClick={() =>
                  handleChange(
                    "placement",
                    VerticalTemplates[selectedTemplateKey]?.type ===
                      "horizontal"
                      ? "bottom"
                      : "right"
                  )
                }
                className={`flex flex-col items-center gap-0.5 transition-all p-3 ${
                  (
                    VerticalTemplates[selectedTemplateKey]?.type ===
                    "horizontal"
                      ? formData.placement === "bottom"
                      : formData.placement === "right"
                  )
                    ? "opacity-100 ring-2 ring-[#783DF9] rounded shadow-[0_0_8px_rgba(120,61,249,0.6)]"
                    : "opacity-60"
                }`}
              >
                <div
                  className={`relative ${
                    (
                      VerticalTemplates[selectedTemplateKey]?.type ===
                      "horizontal"
                        ? formData.placement === "bottom"
                        : formData.placement === "right"
                    )
                      ? "ring-2 ring-[#783DF9] rounded shadow-[0_0_8px_rgba(120,61,249,0.6)]"
                      : ""
                  }`}
                >
                  <img
                    src={
                      VerticalTemplates[selectedTemplateKey]?.type ===
                      "horizontal"
                        ? "/horizontal-bottom.png"
                        : "/vertical-right.svg"
                    }
                    alt={
                      VerticalTemplates[selectedTemplateKey]?.type ===
                      "horizontal"
                        ? "Bottom placement"
                        : "Right placement"
                    }
                    className="w-[104px] h-[76px]"
                  />
                </div>
                <span className="text-white text-[13px] font-medium">
                  {VerticalTemplates[selectedTemplateKey]?.type === "horizontal"
                    ? "Bottom"
                    : "Right"}
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Element Name */}
        <div className="flex flex-col gap-2 mt-4">
          <label className="text-white text-base font-bold leading-[22px] tracking-[-0.18px]">
            Element Name
          </label>
          <input
            type="text"
            placeholder="e.g., Hero Minimal-Launch CTA"
            value={formData.elementName || ""}
            onChange={(e) => handleChange("elementName", e.target.value)}
            maxLength={25}
            className="h-9 px-3 py-2 rounded border border-[#DCDFE3] bg-white text-base text-gray-700 placeholder:text-[#758195] focus:outline-none focus:ring-1 focus:ring-purple-500"
          />
          <div className="flex justify-between items-center text-white text-[9px] font-medium leading-[10px]">
            <span>You can rename it later</span>
            <span>25 characters max</span>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex flex-col pt-8 pb-4">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="h-10 px-3 py-3 flex justify-center items-center gap-2 rounded bg-[#1CD760] text-white text-xs font-bold leading-4 tracking-[-0.12px] hover:bg-[#1ed760] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlacementGroup;
